import Vue from 'vue';
import permitCardService from '@/api/permit-card-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';

const getDefaultPermitCardFormItem = () => ({});

export const getDefaultPermitCardFilterParams = () => ({});

const state = {
  permitCards: null,
  permitCardPagination: {},
  editedPermitCard: {},
  permitCardValidationErrors: {},
  permitCardFilterParams: getDefaultPermitCardFilterParams(),
  newPermitCard: getDefaultPermitCardFormItem(),
};

const getters = {};

const mutations = {
  SET_PERMIT_CARDS(state, { data, current_page, per_page, total }) {
    state.permitCards = data;
    state.permitCardPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_PERMIT_CARD_FILTER_PARAMS(state, params) {
    state.permitCardFilterParams = params;
  },

  SET_EDITED_PERMIT_CARD(state, permitCard) {
    state.permitCardValidationErrors = {};
    state.editedPermitCard = JSON.parse(JSON.stringify(permitCard));
  },

  STORE_PERMIT_CARD(state, permitCard) {
    state.permitCards = [...state.permitCards, permitCard];
    state.permitCardPagination.total += 1;
    state.permitCardValidationErrors = {};
    state.newPermitCard = getDefaultPermitCardFormItem();
  },

  UPDATE_PERMIT_CARD(state, permitCard) {
    state.permitCards = updateArrayItem(state.permitCards, permitCard);
  },

  DELETE_PERMIT_CARD(state, permitCard) {
    state.permitCards = removeArrayItem(state.permitCards, permitCard);
    state.permitCardPagination.total -= 1;
  },

  SET_PERMIT_CARD_VALIDATION_ERRORS(state, permitCardValidationErrors) {
    state.permitCardValidationErrors = permitCardValidationErrors;
  },

  RESET_PERMIT_CARD_VALIDATION_ERRORS(state) {
    state.permitCardValidationErrors = {};
  },

  CLEAR_PERMIT_CARD_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.permitCardValidationErrors, field);
  },
};

const actions = {
  fetchPermitCards({ commit }, params) {
    commit('SET_PERMIT_CARD_FILTER_PARAMS', params);
    return permitCardService.getPage(params).then((res) => {
      commit('SET_PERMIT_CARDS', res.data);
      if (params.page !== state.permitCardFilterParams.page) {
        window.scrollTo(0, 0);
      }
      return res.data;
    });
  },

  storePermitCard({ commit }, permitCard) {
    return permitCardService
      .create(permitCard)
      .then((res) => {
        commit('STORE_PERMIT_CARD', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_card_created'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_CARD_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editPermitCard({ state, commit }, permitCardId) {
    const permitCard = state.permitCards?.find((c) => c.id === permitCardId);
    if (permitCard) {
      commit('SET_EDITED_PERMIT_CARD', permitCard);
      return Promise.resolve(permitCard);
    }
    return permitCardService.getById(permitCardId).then((res) => {
      commit('SET_EDITED_PERMIT_CARD', res.data);
      return res.data;
    });
  },

  updatePermitCard({ commit }, permitCard) {
    return permitCardService
      .update(permitCard)
      .then((res) => {
        commit('UPDATE_PERMIT_CARD', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_card_updated'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_CARD_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  clearPermitCardValidationErrors({ commit }, field) {
    commit('CLEAR_PERMIT_CARD_VALIDATION_ERRORS', field);
  },

  deletePermitCard({ commit }, permitCard) {
    openConfirmDialog({
      title: i18n.t('confirm_permit_card_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      permitCardService.delete(permitCard).then(() => {
        commit('DELETE_PERMIT_CARD', permitCard);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_card_deleted'));
      });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
